import { type Query, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { GuardianContext } from '../components/GuardianProvider';
import type { ClientChatResponse } from '../types/ClientMessage';
import { persistedValueToString } from '../utils/persistedValue';
import { ALL_MESSAGES_ROOTS, CHAT_MESSAGES_ROOT } from './chat/conversationKeys';

type ChatHistory = {
  conversationId: string;
  title: string;
}[];

// Clearing the history must reach the dashboard analyses too, even though they never show
// up in the list below — they are conversations all the same.
const CHAT_QUERY_KEYS = [
  ...ALL_MESSAGES_ROOTS.map((root) => [root]),
  ['chatMetadata'],
  ['chatTitle'],
  ['panels'],
] as const;

export const useChatHistory = () => {
  const queryClient = useQueryClient();

  const cache = queryClient.getQueryCache();
  // all chats that are not in the initial state (i.e. have some messages)
  const chatQueries = cache.findAll({
    queryKey: [CHAT_MESSAGES_ROOT],
    predicate: (query) => !!query.state.dataUpdateCount,
  }) as Query<ClientChatResponse[]>[];

  // all the chat titles associated to the chat queries
  const chatTitleQueries = cache.findAll({
    queryKey: ['chatTitle'],
  }) as Query<string>[];

  const chatHistory: ChatHistory = chatQueries
    .map((query) => {
      const cachedTitle = chatTitleQueries.find((q) => q.queryKey[1] === query.queryKey[1])?.state.data;

      return {
        conversationId: query.queryKey[1] as string,
        title:
          cachedTitle != null
            ? persistedValueToString(cachedTitle)
            : persistedValueToString(query.state.data?.[0]?.content, 35),
        dataUpdatedAt: query.state.dataUpdatedAt,
      };
    })
    .sort((a, b) => b.dataUpdatedAt - a.dataUpdatedAt)
    .map(({ conversationId, title }) => ({ conversationId, title }));

  const { setConversationId } = useContext(GuardianContext);
  const clearCache = useCallback(() => {
    // We must cancel potential ongoing queries before removing them
    // to avoid react-query to re-fetch them after being removed.
    // We also need to clear the localStorage as reloading the app
    // just after clearing the queries would bring them back from localStorage.
    for (const queryKey of CHAT_QUERY_KEYS) {
      queryClient.cancelQueries({ queryKey, exact: false });
      queryClient.removeQueries({ queryKey, exact: false });
    }
    localStorage.clear();
    setConversationId(uuidv4());
  }, []);

  return { chatHistory, clearCache };
};
